package com.imobly.imobly.ui.screens.show.showproperty

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Bathtub
import androidx.compose.material.icons.filled.Bed
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Garage
import androidx.compose.material.icons.filled.RemoveCircle
import androidx.compose.material.icons.filled.SquareFoot
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.compose.rememberNavController
import com.imobly.imobly.domain.Property
import com.imobly.imobly.ui.components.button.ButtonComp
import com.imobly.imobly.ui.components.input.InputDropdownComp
import com.imobly.imobly.ui.components.searchbar.SearchBarComp
import com.imobly.imobly.ui.components.title.TitleComp
import com.imobly.imobly.ui.components.topbar.TopBarComp
import com.imobly.imobly.ui.theme.colors.BackGroundColor
import com.imobly.imobly.ui.theme.colors.PrimaryColor
import com.imobly.imobly.ui.theme.colors.TitleColor
import com.imobly.imobly.ui.theme.fonts.montserratFont
import com.imobly.imobly.viewmodel.PropertyViewModel
import io.kamel.image.KamelImage
import io.kamel.image.asyncPainterResource
import org.jetbrains.compose.ui.tooling.preview.Preview

@Composable
fun ShowPropertiesScreen(propertyViewModel: PropertyViewModel) {

    val properties: MutableState<List<Property>> = remember { propertyViewModel.properties }
    LaunchedEffect(Unit) {
        propertyViewModel.findAllAction()
    }

    Scaffold(topBar = { TopBarComp() }, snackbarHost = {
        SnackbarHost(propertyViewModel.snackMessage.value)
    }, contentWindowInsets = WindowInsets.systemBars, content = { paddingValues ->
        Column(
            modifier = Modifier.background(BackGroundColor).padding(paddingValues).fillMaxSize()
        ) {
            Column(
                modifier = Modifier.fillMaxSize()
            ) {

                TitleComp("Meus Imóveis", {
                    propertyViewModel.goToHome()
                })

                Box(
                    modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center
                ) {
                    ButtonComp(
                        "Cadastrar imóvel",
                        { Icon(Icons.Default.Add, "Sinal de soma") },
                        PrimaryColor,
                        { propertyViewModel.goToCreateProperty() }
                    )
                }

                Box(
                    modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center
                ) {
                    SearchBarComp(
                        "Buscar imóveis pelo título",
                        propertyViewModel.searchText.value,
                        { propertyViewModel.changeSearchText(it) },
                        { propertyViewModel.searchAction() }
                    )
                }

                Spacer(modifier = Modifier.height(10.dp))

                FlowRow(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp),
                    horizontalArrangement = Arrangement.Center,
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    InputDropdownComp(
                        label = "Status",
                        options = propertyViewModel.activeOptions(),
                        selectedOption = propertyViewModel.selectedOption(),
                        onOptionSelected = { selectedOption ->
                            propertyViewModel.onOptionSelectedActive(selectedOption)
                        },
                        modifier = Modifier
                            .padding(4.dp)
                            .widthIn(max = 400.dp, min = 260.dp)
                    )

                    InputDropdownComp(
                        label = "Categoria",
                        options = propertyViewModel.categoriesOptions(),
                        selectedOption = propertyViewModel.categoryTitleSelected.value,
                        onOptionSelected = { selectedOption ->
                            propertyViewModel.onOptionSelectedCategory(selectedOption)
                        },
                        modifier = Modifier
                            .padding(4.dp)
                            .widthIn(max = 400.dp, min = 260.dp)
                    )
                }

                Spacer(modifier = Modifier.height(10.dp))

                LazyColumn(
                    modifier = Modifier.fillMaxWidth().weight(1f),
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    contentPadding = PaddingValues(horizontal = 8.dp, vertical = 8.dp)
                ) {
                    items(properties.value) { property ->
                        PropertyCardComp(property, propertyViewModel)
                    }
                }

                Spacer(modifier = Modifier.height(8.dp))

                val totalPages = propertyViewModel.totalPages.value
                if (totalPages > 1) {
                    Box(
                        modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        LazyRow(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            items(totalPages) { pageIndex ->
                                val isSelected = propertyViewModel.currentPage.value == pageIndex
                                Surface(
                                    onClick = { propertyViewModel.loadPage(pageIndex) },
                                    shape = RoundedCornerShape(50),
                                    color = if (isSelected) PrimaryColor else Color.White,
                                    shadowElevation = 2.dp,
                                    modifier = Modifier.size(40.dp),
                                    border = if (!isSelected) BorderStroke(1.dp, Color(0xFFCCCCCC)) else null
                                ) {
                                    Box(contentAlignment = Alignment.Center) {
                                        Text(
                                            text = "${pageIndex + 1}",
                                            color = if (isSelected) Color.White else TitleColor,
                                            fontFamily = montserratFont(),
                                            fontWeight = FontWeight.Bold,
                                            fontSize = 14.sp
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

            }
        }
    })
}

@Composable
fun PropertyCardComp(property: Property, propertyViewModel: PropertyViewModel) {
    Card(
        modifier = Modifier.widthIn(max = 1000.dp).fillMaxWidth().padding(horizontal = 4.dp),
        colors = CardDefaults.cardColors(containerColor = Color(0xFFFFFFFF)),
        elevation = CardDefaults.cardElevation(defaultElevation = 6.dp),
        shape = RoundedCornerShape(16.dp)
    ) {
        Column(modifier = Modifier.fillMaxSize()) {
            Box(
                modifier = Modifier.fillMaxWidth().height(180.dp)
                    .clip(RoundedCornerShape(topStart = 16.dp, topEnd = 16.dp)), contentAlignment = Alignment.Center
            ) {

                KamelImage(
                    resource = { asyncPainterResource(property.pathImages.first()) },
                    contentDescription = "Imagem do imóvel ${property.title}",
                    modifier = Modifier.fillMaxSize().clip(RoundedCornerShape(topStart = 16.dp, topEnd = 16.dp)),
                    contentScale = ContentScale.Crop,
                    onLoading = { progress -> CircularProgressIndicator({ progress }) },
                    onFailure = { CircularProgressIndicator() })


                Box(
                    modifier = Modifier.align(Alignment.TopEnd).padding(12.dp).background(
                        color = Color(0xFFF2603F), shape = RoundedCornerShape(8.dp)
                    ).padding(horizontal = 12.dp, vertical = 6.dp)
                ) {
                    Text(
                        text = "R$ ${property.monthlyRent}",
                        fontWeight = FontWeight.Bold,
                        fontSize = 14.sp,
                        color = Color.White,
                        fontFamily = montserratFont()
                    )
                }
            }

            Column(
                modifier = Modifier.fillMaxSize().padding(16.dp)
            ) {
                Text(
                    text = property.title,
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    color = Color(0xFF2D5B7A),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    fontFamily = montserratFont()
                )

                Spacer(modifier = Modifier.height(8.dp))

                Text(
                    text = "${property.address.state}/${property.address.city}, ${property.address.street}, ${property.address.number} | ${property.address.complement}",
                    fontWeight = FontWeight.Normal,
                    fontSize = 14.sp,
                    color = Color(0xFF666666),
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                    fontFamily = montserratFont()
                )

                Spacer(modifier = Modifier.height(16.dp))

                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        TextInfoComp(
                            "Área: ${property.area} m²", { Icon(Icons.Default.SquareFoot, "Área") }
                        )
                    }
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        TextInfoComp(
                            "Nº quartos: ${property.bedrooms}", { Icon(Icons.Default.Bed, "Quartos") }
                        )
                    }
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        TextInfoComp(
                            "Vagas: ${property.garageSpaces}", { Icon(Icons.Default.Garage, "Garagem") }
                        )
                    }
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        TextInfoComp(
                            "Nº banheiros: ${property.bathrooms}", { Icon(Icons.Default.Bathtub, "Banheiros") }
                        )
                    }
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        TextInfoComp(
                            if (property.isEnabled) "Ativo" else "Inativo",
                            {
                                if (property.isEnabled) {
                                    Icon(Icons.Default.CheckCircle, "Enabled")
                                } else {
                                    Icon(Icons.Default.RemoveCircle, "Disabled")
                                }
                            }
                        )
                    }
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        TextInfoComp(
                            if (property.isRentedOut) "Alugado" else "Disponível",
                            {
                                if (property.isRentedOut) {
                                    Icon(Icons.Default.CheckCircle, "rented")
                                } else {
                                    Icon(Icons.Default.RemoveCircle, "vacated")
                                }
                            }
                        )
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))

                Button(
                    onClick = {
                        propertyViewModel.goToEditProperty(property)
                    }, modifier = Modifier.fillMaxWidth().height(42.dp), colors = ButtonDefaults.buttonColors(
                        containerColor = Color(0xFFF2603F)
                    ), shape = RoundedCornerShape(12.dp)
                ) {
                    Text(
                        text = "Ver Detalhes",
                        fontWeight = FontWeight.Bold,
                        fontSize = 14.sp,
                        fontFamily = montserratFont()
                    )
                }
            }
        }
    }
}

@Composable
fun TextInfoComp(text: String, icon: @Composable () -> Unit) {
    Box(
        modifier = Modifier.size(20.dp).background(
            color = Color(0xFFF2603F).copy(alpha = 0.2f), shape = CircleShape
        ), contentAlignment = Alignment.Center
    ) {
        icon()
    }

    Spacer(modifier = Modifier.width(8.dp))

    Text(
        text = text,
        fontWeight = FontWeight.Medium,
        fontSize = 14.sp,
        color = Color(0xFF333333),
        fontFamily = montserratFont()
    )
}

@Preview
@Composable
fun ShowPropertiesScreenPreview() {
    val navControllerFake = rememberNavController()
    ShowPropertiesScreen(PropertyViewModel(navControllerFake))
}
